import React, { useEffect, useState, useContext } from 'react';
import authFetch from '../api';
import { useTranslation } from 'react-i18next';
import { 
  PlusCircle, 
  Pencil, 
  Search, 
  Briefcase, 
  ChevronDown, 
  ChevronRight, 
  BarChart3, 
  Users,
  Filter,
  Download,
  Trash2,
  Edit,
  Eye,
  FileText,
  Settings,
  Calendar,
  DollarSign,
  User,
  Building,
  FileUp,
  X,
  CheckCircle,
  AlertCircle,
  Clock,
  FolderOpen
} from 'lucide-react';
import { AuthContext } from '../context/AuthContext';

const WorkersPage = () => {
  const { t } = useTranslation();
  // Main state
  const [activeTab, setActiveTab] = useState('workers'); // 'workers', 'projects', 'archived'
  const [showModal, setShowModal] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Workers data
  const [workersWithHours, setWorkersWithHours] = useState([]);
  const [expandedWorkers, setExpandedWorkers] = useState(new Set());
  
  // Projects data
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [projectWorkers, setProjectWorkers] = useState([]);
  const [projectStats, setProjectStats] = useState(null);
  
  // Monthly duty view data
  const [monthlyDutyData, setMonthlyDutyData] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [dutyViewMode, setDutyViewMode] = useState('hours'); // 'status', 'hours', 'percentage'
  const [showWeekends, setShowWeekends] = useState(true);
  const [showHolidays, setShowHolidays] = useState(true);
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    contact: '',
    status: 'Active',
    contractFrom: '',
  });

  const { user: currentUser } = useContext(AuthContext);
  const isAdmin = currentUser?.role === 'Admin' || currentUser?.role === 'SuperAdmin';
  const readonly = currentUser?.access?.readOnly || false;
  const canAccessWorkers = currentUser?.access?.pages?.workers !== false;
  const canEdit = !readonly && canAccessWorkers;
  const hideWorkersPage = readonly && !canAccessWorkers;

  useEffect(() => {
    if (activeTab === 'workers' || activeTab === 'archived') {
      fetchWorkersWithHours();
    } else if (activeTab === 'projects') {
      fetchProjects();
    }
  }, [activeTab]);

  useEffect(() => {
    if (selectedProject && activeTab === 'projects') {
      fetchProjectWorkers(selectedProject);
    }
  }, [selectedProject, activeTab]);

  useEffect(() => {
    if (selectedProject && activeTab === 'projects') {
      fetchMonthlyDutyData(selectedProject, selectedYear, selectedMonth);
    }
  }, [selectedProject, selectedYear, selectedMonth, activeTab]);

  // Data fetching functions
  const fetchWorkersWithHours = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching workers with hours...');
      
      const res = await authFetch('/workers/with-hours');
      const data = res.workers || res.data?.workers || res;
      
      console.log('�� Workers with hours received:', data.length);
      setWorkersWithHours(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("❌ Error fetching workers with hours:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      console.log('🔍 Fetching projects for picker...');
      const res = await authFetch('/projects/list');
      const data = res.projects || res.data?.projects || res;
      
      console.log('📊 Projects received:', data.length);
      setProjects(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("❌ Error fetching projects:", err);
    }
  };

  const fetchProjectWorkers = async (projectId) => {
    try {
      setLoading(true);
      console.log('🔍 Fetching workers for project:', projectId);
      
      const res = await authFetch(`/workers/project/${projectId}`);
      console.log('📊 Raw API response:', res);
      
      // Handle different response structures
      let data;
      if (res && res.data) {
        data = res.data;
      } else if (res && res.workers) {
        data = res;
      } else {
        data = res;
      }
      
      console.log('📊 Processed data:', data);
      
      // Add null checks for all properties
      const workers = data?.workers || [];
      const project = data?.project || {};
      const totalProjectHours = data?.totalProjectHours || 0;
      const averageHours = data?.averageHours || 0;
      const additionalHours = data?.additionalHours || 0;
      
      console.log('📊 Project workers received:', workers.length);
      console.log('📊 Project data:', project);
      console.log('📊 Total hours:', totalProjectHours);
      console.log('📊 Average hours:', averageHours);
      console.log('📊 Additional hours:', additionalHours);
      
      setProjectWorkers(workers);
      setProjectStats({
        project: project,
        totalProjectHours: totalProjectHours,
        averageHours: averageHours,
        additionalHours: additionalHours
      });
    } catch (err) {
      console.error("❌ Error fetching project workers:", err);
      console.error('Error details:', {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data
      });
      
      // Handle specific error cases
      if (err.response?.status === 404) {
        console.log('❌ Project not found');
        setProjectWorkers([]);
        setProjectStats({
          project: { name: 'Project Not Found' },
          totalProjectHours: 0,
          averageHours: 0,
          additionalHours: 0
        });
      } else {
        setProjectWorkers([]);
        setProjectStats(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchMonthlyDutyData = async (projectId, year, month) => {
    try {
      console.log('🔍 Fetching monthly duty data for project:', projectId, 'Year:', year, 'Month:', month);
      
      const res = await authFetch(`/workers/monthly-duty/${projectId}/${year}/${month}`);
      console.log('📊 Monthly duty data received:', res);
      console.log('📊 Response type:', typeof res);
      console.log('📊 Response keys:', res ? Object.keys(res) : 'null');
      console.log('📊 Month data:', res?.month);
      console.log('📊 Workers data:', res?.workers);
      console.log('📊 Workers length:', res?.workers?.length);
      
      // Handle different response structures
      let data = res;
      if (res && res.data) {
        data = res.data;
        console.log('📊 Using res.data:', data);
      } else if (res && res.response && res.response.data) {
        data = res.response.data;
        console.log('�� Using res.response.data:', data);
      }
      
      console.log('📊 Final data to use:', data);
      console.log('�� Final data keys:', data ? Object.keys(data) : 'null');
      
      // Validate response structure
      if (!data || !data.month || !data.workers) {
        console.error('❌ Invalid response structure:', data);
        console.error('❌ Missing month:', !data?.month);
        console.error('❌ Missing workers:', !data?.workers);
        setMonthlyDutyData(null);
        return;
      }
      
      console.log('✅ Setting monthly duty data with:', {
        month: data.month,
        workersCount: data.workers.length,
        hasMonthStats: !!data.monthStats,
        hasDailyTotals: !!data.dailyTotals
      });
      
      setMonthlyDutyData(data);
    } catch (err) {
      console.error("❌ Error fetching monthly duty data:", err);
      console.error('Error details:', {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data
      });
      setMonthlyDutyData(null);
    }
  };

  // Helper functions
  const toggleWorkerExpansion = (workerId) => {
    const newExpanded = new Set(expandedWorkers);
    if (newExpanded.has(workerId)) {
      newExpanded.delete(workerId);
    } else {
      newExpanded.add(workerId);
    }
    setExpandedWorkers(newExpanded);
  };

  const formatHours = (hours) => {
    return hours ? `${hours.toFixed(1)}h` : '0h';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'bg-green-100 text-green-800';
      case 'Inactive': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Active': return <CheckCircle size={16} />;
      case 'Inactive': return <X size={16} />;
      default: return <Clock size={16} />;
    }
  };

  const getStatusTranslation = (status) => {
    switch (status) {
      case 'Active': return t('workers.statusActive');
      case 'Inactive': return t('workers.statusInactive');
      default: return status;
    }
  };

  // Monthly duty helper functions
  const getDutyStatusClass = (hours, year, month, day) => {
    const currentDate = new Date();
    const cellDate = new Date(year, month - 1, day);
    const isPastOrToday = cellDate <= currentDate;
    
    if (!hours || hours === 0) {
      return isPastOrToday ? 'bg-red-100 text-red-800' : 'bg-gray-50 text-gray-400';
    }
    if (hours < 4) return 'bg-yellow-100 text-yellow-800';
    if (hours > 8) return 'bg-blue-100 text-blue-800';
    return 'bg-green-100 text-green-800';
  };

  const getDutyDisplayValue = (hours, mode) => {
    if (!hours || hours === 0) return '-';
    switch (mode) {
      case 'hours':
        return `${hours.toFixed(1)}h`;
      case 'percentage':
        return `${((hours / 8) * 100).toFixed(0)}%`;
      default:
        return hours > 8 ? 'OT' : hours < 4 ? 'PT' : '✓';
    }
  };

  const isWeekend = (year, month, day) => {
    const date = new Date(year, month - 1, day);
    return date.getDay() === 0 || date.getDay() === 6;
  };

  const getMonthName = (month) => {
    const months = [
      t('workers.january'), t('workers.february'), t('workers.march'), t('workers.april'), t('workers.may'), t('workers.june'),
      t('workers.july'), t('workers.august'), t('workers.september'), t('workers.october'), t('workers.november'), t('workers.december')
    ];
    return months[month - 1];
  };

  const navigateMonth = (direction) => {
    let newYear = selectedYear;
    let newMonth = selectedMonth;
    
    if (direction === 'next') {
      if (selectedMonth === 12) {
        newMonth = 1;
        newYear++;
      } else {
        newMonth++;
      }
    } else {
      if (selectedMonth === 1) {
        newMonth = 12;
        newYear--;
      } else {
        newMonth--;
      }
    }
    
    setSelectedYear(newYear);
    setSelectedMonth(newMonth);
  };

  // Filtering and sorting
  const getFilteredWorkers = () => {
    let filtered = workersWithHours.filter(w => {
      if (activeTab === 'workers') return w.status === 'Active';
      if (activeTab === 'archived') return w.status === 'Inactive';
      return true;
    });

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(w => 
        w.name?.toLowerCase().includes(q) ||
        w.location?.toLowerCase().includes(q) ||
        w.contact?.toLowerCase().includes(q) ||
        w.projects?.some(p => p.projectName.toLowerCase().includes(q))
      );
    }

    return filtered.sort((a, b) => b.totalHours - a.totalHours);
  };

  // Excel upload and form handling
  const handleExcelUpload = async (e) => {
    if (readonly) {
      alert(t('workers.noPermissionToEdit'));
      e.target.value = null;
      return;
    }
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      console.log('🔍 Uploading Excel file...');
      const res = await authFetch.post('/workers/upload-excel', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      let result;
      if (res && res.data) {
        result = res.data;
      } else {
        result = res;
      }
      
      console.log('📊 Upload result:', result);
      alert(t('workers.importComplete', { added: result.added, skipped: result.skipped }));
      fetchWorkersWithHours();
    } catch (err) {
      console.error("❌ Excel upload failed:", err);
      alert(t('workers.importFailed'));
    }
  };

  const handleSubmit = async () => {
    if (readonly) {
      alert(t('workers.noPermissionToEdit'));
      return;
    }
    try {
      console.log('🔍 Saving worker:', formData);
      
      if (editIndex) {
        await authFetch.patch(`/workers/${editIndex}`, formData);
      } else {
        await authFetch.post('/workers', formData);
      }

      console.log('✅ Worker saved successfully');
      setFormData({ name: '', location: '', contact: '', status: 'Active', contractFrom: '' });
      setEditIndex(null);
      setShowModal(false);
      fetchWorkersWithHours();
    } catch (err) {
      console.error("❌ Error saving worker:", err);
    }
  };

  const handleEdit = (worker) => {
    if (readonly) {
      alert(t('workers.noPermissionToEdit'));
      return;
    }
    setFormData({
      name: worker.name,
      location: worker.location,
      contact: worker.contact,
      status: worker.status,
      contractFrom: worker.contractFrom?.slice(0, 10) || '',
    });
    setEditIndex(worker._id);
    setShowModal(true);
  };

  const filteredWorkers = getFilteredWorkers();

  return hideWorkersPage ? null : (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center pt-16">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('workers.workersManagement')}</h1>
            <p className="text-gray-600 mt-2">{t('workers.manageWorkersAnalyzeProjects')}</p>
          </div>
          {!readonly && canEdit && activeTab !== 'projects' && (
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setFormData({ name: '', location: '', contact: '', status: 'Active', contractFrom: '' });
                  setEditIndex(null);
                  setShowModal(true);
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors"
                title={t('workers.addWorker')}
              >
                <PlusCircle size={20} />
                {t('workers.addWorker')}
              </button>
              <input
                type="file"
                accept=".xlsx"
                id="excelUpload"
                className="hidden"
                onChange={handleExcelUpload}
              />
              <button
                onClick={() => {
                  document.getElementById('excelUpload').click();
                }}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors"
                title={t('workers.importExcel')}
              >
                <FileUp size={20} />
                {t('workers.importExcel')}
              </button>
            </div>
          )}
        </div>

        {/* Search Bar */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder={readonly ? t('workers.readOnlyMode') : t('workers.searchWorkers')}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={readonly}
              title={readonly ? t('workers.searchDisabledReadOnly') : ""}
            />
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('workers')}
              disabled={readonly}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'workers'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              title={readonly ? t('workers.tabSwitchingDisabledReadOnly') : ""}
            >
              <div className="flex items-center gap-2">
                <Users size={16} />
                {t('workers.activeWorkers')}
              </div>
            </button>
            <button
              onClick={() => setActiveTab('projects')}
              disabled={readonly}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'projects'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              title={readonly ? t('workers.tabSwitchingDisabledReadOnly') : ""}
            >
              <div className="flex items-center gap-2">
                <BarChart3 size={16} />
                {t('workers.workerAnalysis')}
              </div>
            </button>
            <button
              onClick={() => setActiveTab('archived')}
              disabled={readonly}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'archived'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              title={readonly ? t('workers.tabSwitchingDisabledReadOnly') : ""}
            >
              <div className="flex items-center gap-2">
                <Briefcase size={16} />
                {t('workers.archivedWorkers')}
              </div>
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'workers' && (
          <div className="space-y-6">
            {/* Workers Overview Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-blue-100">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8"></th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('workers.workerName')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('workers.location')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('workers.contact')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('workers.status')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('workers.totalHours')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('workers.projects')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('workers.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {loading ? (
                      <tr>
                        <td colSpan="8" className="px-6 py-12 text-center">
                          <Users className="mx-auto h-12 w-12 text-gray-400" />
                          <h3 className="mt-2 text-sm font-medium text-gray-900">{t('workers.loadingWorkersData')}</h3>
                        </td>
                      </tr>
                    ) : filteredWorkers.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="px-6 py-12 text-center">
                          <Users className="mx-auto h-12 w-12 text-gray-400" />
                          <h3 className="mt-2 text-sm font-medium text-gray-900">{t('workers.noWorkersFound')}</h3>
                          <p className="mt-1 text-sm text-gray-500">
                            {searchQuery 
                              ? t('workers.tryAdjustingSearchCriteria')
                              : t('workers.getStartedAddingNewWorker')
                            }
                          </p>
                        </td>
                      </tr>
                    ) : (
                      filteredWorkers.map((worker, idx) => (
                        <React.Fragment key={worker._id}>
                          <tr className="hover:bg-gray-50 transition">
                            <td className="px-6 py-4">
                              {worker.projects && worker.projects.length > 0 && (
                                <button
                                  onClick={() => toggleWorkerExpansion(worker._id)}
                                  className="text-gray-500 hover:text-gray-700"
                                >
                                  {expandedWorkers.has(worker._id) ? (
                                    <ChevronDown size={16} />
                                  ) : (
                                    <ChevronRight size={16} />
                                  )}
                                </button>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {worker.name}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {worker.location}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {worker.contact}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(worker.status)}`}>
                                {getStatusIcon(worker.status)}
                                <span className="ml-1">{getStatusTranslation(worker.status)}</span>
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                              {formatHours(worker.totalHours)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {worker.projects?.length || 0} {worker.projects?.length !== 1 ? t('workers.projects') : t('workers.project')}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex space-x-2">
                                {!readonly && canEdit && (
                                  <button
                                    onClick={e => { e.stopPropagation(); handleEdit(worker); }}
                                    className="text-indigo-600 hover:text-indigo-900"
                                    title={t('workers.editWorker')}
                                  >
                                    <Edit size={16} />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                          {/* Expanded project details */}
                          {expandedWorkers.has(worker._id) && worker.projects && worker.projects.length > 0 && (
                            <tr className="bg-gray-50">
                              <td colSpan="8" className="px-6 py-4">
                                <div className="ml-6">
                                  <h4 className="font-medium text-gray-700 mb-3">{t('workers.projectDetails')}:</h4>
                                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {worker.projects.map((project, pIdx) => (
                                      <div key={pIdx} className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                                        <div className="font-medium text-gray-900">{project.projectName}</div>
                                        <div className="text-sm text-green-600 font-semibold">
                                          {formatHours(project.hours)}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="text-sm text-gray-600 font-semibold">
              {t('workers.totalWorkers')}: {filteredWorkers.length}
            </div>
          </div>
        )}

        {activeTab === 'projects' && (
          <div className="space-y-6">
            {/* Project Picker */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 mb-4">{t('workers.workerAnalysis')}</h3>
                <div className="flex gap-4 items-center">
                  <label className="text-sm font-medium text-gray-700">{t('workers.selectProject')}:</label>
                  <select
                    value={selectedProject}
                    onChange={(e) => setSelectedProject(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={readonly}
                  >
                    <option value="">{t('workers.chooseAProject')}</option>
                    {projects.map((project) => (
                      <option key={project._id} value={project._id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Project Statistics */}
            {projectStats && projectStats.project ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
                  <h4 className="text-sm font-medium text-gray-500 uppercase">{t('workers.project')}</h4>
                  <p className="text-2xl font-bold text-gray-900">{projectStats.project.name || t('workers.unknownProject')}</p>
                </div>
                <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
                  <h4 className="text-sm font-medium text-gray-500 uppercase">{t('workers.totalHours')}</h4>
                  <p className="text-2xl font-bold text-green-600">{formatHours(projectStats.totalProjectHours)}</p>
                </div>
                <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
                  <h4 className="text-sm font-medium text-gray-500 uppercase">{t('workers.additionalWorkingHours')}</h4>
                  <p className="text-2xl font-bold text-orange-600">{formatHours(projectStats.additionalHours)}</p>
                </div>
              </div>
            ) : selectedProject && !loading ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex">
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      {t('workers.noProjectDataAvailable')}
                    </h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p>{t('workers.projectNoWorkingHoursRecorded')}</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {/* Monthly Duty View Controls */}
            {selectedProject && (
              <div className="bg-white rounded-lg shadow p-6 mb-6 border border-gray-200">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  {/* Month Navigation */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => navigateMonth('prev')}
                      className="px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                      disabled={readonly}
                    >
                      ←
                    </button>
                    <span className="text-lg font-semibold text-gray-900">
                      {getMonthName(selectedMonth)} {selectedYear}
                    </span>
                    <button
                      onClick={() => navigateMonth('next')}
                      className="px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                      disabled={readonly}
                    >
                      →
                    </button>
                  </div>

                  {/* View Mode Selector */}
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700">{t('workers.view')}:</label>
                    <select
                      value={dutyViewMode}
                      onChange={(e) => setDutyViewMode(e.target.value)}
                      className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      disabled={readonly}
                    >
                      <option value="status">{t('workers.status')}</option>
                      <option value="hours">{t('workers.hours')}</option>
                      <option value="percentage">{t('workers.percentage')}</option>
                    </select>
                  </div>

                  {/* Display Options */}
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={showWeekends}
                        onChange={(e) => setShowWeekends(e.target.checked)}
                        disabled={readonly}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      {t('workers.showWeekends')}
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={showHolidays}
                        onChange={(e) => setShowHolidays(e.target.checked)}
                        disabled={readonly}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      {t('workers.showHolidays')}
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Monthly Duty Table */}
            {selectedProject && monthlyDutyData && monthlyDutyData.month && (
              <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10 min-w-[150px]">
                          {t('workers.worker')}
                        </th>
                        {Array.from({ length: monthlyDutyData.month.daysInMonth || 31 }, (_, i) => i + 1).map(day => {
                          const isWeekendDay = isWeekend(selectedYear, selectedMonth, day);
                          if (!showWeekends && isWeekendDay) return null;
                          
                          return (
                            <th key={day} className={`px-1 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider ${isWeekendDay ? 'bg-blue-100' : ''}`}>
                              {day}
                            </th>
                          );
                        }).filter(Boolean)}
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50">{t('workers.monthlyTotals')}</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {loading ? (
                        <tr>
                          <td colSpan={(monthlyDutyData.month.daysInMonth || 31) + 2} className="px-6 py-12 text-center">
                            <BarChart3 className="mx-auto h-12 w-12 text-gray-400" />
                            <h3 className="mt-2 text-sm font-medium text-gray-900">{t('workers.loadingMonthlyDutyData')}</h3>
                          </td>
                        </tr>
                      ) : (monthlyDutyData.workers || []).length === 0 ? (
                        <tr>
                          <td colSpan={(monthlyDutyData.month.daysInMonth || 31) + 2} className="px-6 py-12 text-center">
                            <BarChart3 className="mx-auto h-12 w-12 text-gray-400" />
                            <h3 className="mt-2 text-sm font-medium text-gray-900">{t('workers.noWorkersFound')}</h3>
                            <p className="mt-1 text-sm text-gray-500">
                              {t('workers.noWorkersFoundForProject', { month: getMonthName(selectedMonth), year: selectedYear })}
                            </p>
                          </td>
                        </tr>
                      ) : (
                        <>
                          {/* Worker Rows */}
                          {(monthlyDutyData.workers || []).map((worker, idx) => (
                            <tr key={worker.workerId} className="hover:bg-gray-50 transition">
                              <td className="px-6 py-4 font-medium sticky left-0 bg-white z-10 min-w-[150px] border-r">
                                <div className="text-sm font-semibold text-gray-900">{worker.workerName}</div>
                                <div className="text-xs text-gray-500">{worker.location}</div>
                              </td>
                              {Array.from({ length: monthlyDutyData.month.daysInMonth || 31 }, (_, i) => i + 1).map(day => {
                                const isWeekendDay = isWeekend(selectedYear, selectedMonth, day);
                                if (!showWeekends && isWeekendDay) return null;
                                
                                const hours = (worker.dailyHours && worker.dailyHours[day]) || 0;
                                return (
                                  <td key={day} className={`px-1 py-1 text-center text-xs border ${getDutyStatusClass(hours, selectedYear, selectedMonth, day)} ${isWeekendDay ? 'bg-blue-50' : ''}`}>
                                    {getDutyDisplayValue(hours, dutyViewMode)}
                                  </td>
                                );
                              }).filter(Boolean)}
                              <td className="px-6 py-4 font-semibold text-green-600 bg-green-50">
                                <div className="text-sm">{formatHours((worker.monthlyTotals && worker.monthlyTotals.totalHours) || 0)}</div>
                                <div className="text-xs text-gray-500">{(worker.monthlyTotals && worker.monthlyTotals.daysWorked) || 0} {(worker.monthlyTotals && worker.monthlyTotals.daysWorked) !== 1 ? t('workers.days') : t('workers.day')}</div>
                              </td>
                            </tr>
                          ))}
                          
                          {/* Daily Totals Row */}
                          <tr className="border-t-2 border-gray-300 bg-gray-50">
                            <td className="px-6 py-4 font-bold text-gray-900 sticky left-0 bg-gray-50 z-10 border-r">
                              {t('workers.dailyTotals')}
                            </td>
                            {Array.from({ length: monthlyDutyData.month.daysInMonth || 31 }, (_, i) => i + 1).map(day => {
                              const isWeekendDay = isWeekend(selectedYear, selectedMonth, day);
                              if (!showWeekends && isWeekendDay) return null;
                              
                              const dayData = (monthlyDutyData.dailyTotals && monthlyDutyData.dailyTotals[day]) || { workers: 0, totalHours: 0 };
                              return (
                                <td key={day} className={`px-1 py-1 text-center text-xs font-semibold ${isWeekendDay ? 'bg-blue-100' : ''}`}>
                                  <div>{dayData.workers || 0}</div>
                                  <div className="text-gray-500">{formatHours(dayData.totalHours || 0)}</div>
                                </td>
                              );
                            }).filter(Boolean)}
                            <td className="px-6 py-4 font-bold text-gray-900 bg-blue-50">
                              <div className="text-sm">{(monthlyDutyData.monthStats && monthlyDutyData.monthStats.totalWorkers) || 0}</div>
                              <div className="text-xs text-gray-500">{formatHours((monthlyDutyData.monthStats && monthlyDutyData.monthStats.totalMonthHours) || 0)}</div>
                            </td>
                          </tr>
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Monthly Statistics */}
            {selectedProject && monthlyDutyData && monthlyDutyData.monthStats && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
                <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
                  <h4 className="text-sm font-medium text-gray-500 uppercase">{t('workers.totalWorkers')}</h4>
                  <p className="text-2xl font-bold text-gray-900">{monthlyDutyData.monthStats.totalWorkers || 0}</p>
                </div>
                <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
                  <h4 className="text-sm font-medium text-gray-500 uppercase">{t('workers.workingDays')}</h4>
                  <p className="text-2xl font-bold text-green-600">{monthlyDutyData.monthStats.workingDays || 0}</p>
                </div>
                <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
                  <h4 className="text-sm font-medium text-gray-500 uppercase">{t('workers.totalHours')}</h4>
                  <p className="text-2xl font-bold text-blue-600">{formatHours(monthlyDutyData.monthStats.totalMonthHours || 0)}</p>
                </div>
                <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
                  <h4 className="text-sm font-medium text-gray-500 uppercase">{t('workers.avgDailyWorkers')}</h4>
                  <p className="text-2xl font-bold text-purple-600">{(monthlyDutyData.monthStats.averageDailyWorkers || 0).toFixed(1)}</p>
                </div>
              </div>
            )}

            {/* No Data Message */}
            {selectedProject && !monthlyDutyData && !loading && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex">
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      {t('workers.noMonthlyDutyDataAvailable')}
                    </h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p>{t('workers.projectNoWorkingHoursRecordedForMonth', { month: getMonthName(selectedMonth), year: selectedYear })}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'archived' && (
          <div className="space-y-6">
            {/* Search Bar */}
            <div className="flex flex-col sm:flex-row gap-4">
                          <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder={readonly ? t('workers.readOnlyMode') : t('workers.searchArchivedWorkers')}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={readonly}
                title={readonly ? t('workers.searchDisabledReadOnly') : ""}
              />
            </div>
            </div>

            {/* Archived Workers Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('workers.workerName')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('workers.location')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('workers.contact')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('workers.status')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('workers.contractFrom')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('workers.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {loading ? (
                      <tr>
                        <td colSpan="6" className="px-6 py-12 text-center">
                          <Briefcase className="mx-auto h-12 w-12 text-gray-400" />
                          <h3 className="mt-2 text-sm font-medium text-gray-900">{t('workers.loadingArchivedWorkers')}</h3>
                        </td>
                      </tr>
                    ) : filteredWorkers.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-6 py-12 text-center">
                          <Briefcase className="mx-auto h-12 w-12 text-gray-400" />
                          <h3 className="mt-2 text-sm font-medium text-gray-900">{t('workers.noArchivedWorkersFound')}</h3>
                          <p className="mt-1 text-sm text-gray-500">
                            {searchQuery 
                              ? t('workers.tryAdjustingSearchCriteria')
                              : t('workers.noWorkersArchivedYet')
                            }
                          </p>
                        </td>
                      </tr>
                    ) : (
                      filteredWorkers.map((worker, idx) => (
                        <tr key={worker._id} className="hover:bg-gray-50 transition">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {worker.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {worker.location}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {worker.contact}
                          </td>
                                                  <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(worker.status)}`}>
                            {getStatusIcon(worker.status)}
                            <span className="ml-1">{getStatusTranslation(worker.status)}</span>
                          </span>
                        </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {worker.contractFrom ? new Date(worker.contractFrom).toLocaleDateString('bg-BG') : ''}
                          </td>
                                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            {!readonly && canEdit && (
                              <button
                                onClick={e => { e.stopPropagation(); handleEdit(worker); }}
                                className="text-indigo-600 hover:text-indigo-900"
                                title={t('workers.editWorker')}
                              >
                                <Edit size={16} />
                              </button>
                            )}
                          </div>
                        </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="text-sm text-gray-600 font-semibold">
              {t('workers.totalArchivedWorkers')}: {filteredWorkers.length}
            </div>
          </div>
        )}
      </div>

      {/* Edit/Add Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editIndex ? t('workers.editWorker') : t('workers.addNewWorker')}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('workers.name')}</label>
                  <input
                    type="text"
                    placeholder={t('workers.enterWorkerName')}
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={readonly}
                    title={readonly ? t('workers.inputDisabledReadOnly') : ""}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('workers.location')}</label>
                  <input
                    type="text"
                    placeholder={t('workers.cityVillage')}
                    value={formData.location}
                    onChange={e => setFormData({ ...formData, location: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={readonly}
                    title={readonly ? t('workers.inputDisabledReadOnly') : ""}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('workers.contact')}</label>
                  <input
                    type="text"
                    placeholder={t('workers.phoneNumberOrEmail')}
                    value={formData.contact}
                    onChange={e => setFormData({ ...formData, contact: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={readonly}
                    title={readonly ? t('workers.inputDisabledReadOnly') : ""}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('workers.status')}</label>
                  <select
                    value={formData.status}
                    onChange={e => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={readonly}
                    title={readonly ? t('workers.selectDisabledReadOnly') : ""}
                  >
                    <option value="Active">{t('workers.statusActive')}</option>
                    <option value="Inactive">{t('workers.statusInactive')}</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('workers.contractFrom')}</label>
                  <input
                    type="date"
                    value={formData.contractFrom}
                    onChange={e => setFormData({ ...formData, contractFrom: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={readonly}
                    title={readonly ? t('workers.inputDisabledReadOnly') : ""}
                  />
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                    disabled={readonly}
                    title={readonly ? t('workers.cancelDisabledReadOnly') : ""}
                  >
                    {t('workers.cancel')}
                  </button>
                  {canEdit && !readonly && (
                    <button
                      onClick={handleSubmit}
                      className="bg-green-600 text-white px-3 py-2 rounded hover:bg-green-700 text-base"
                      disabled={readonly}
                      title={readonly ? t('workers.saveDisabledReadOnly') : ""}
                    >
                      {t('workers.save')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WorkersPage;
